/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// utils
import UuidUtils from "utils/uuid-utils/UuidUtils.js";

// cached functions
const hasProperty = Object.prototype.hasOwnProperty;

class ValidationUtils {
  static MAX_LENGTH_BIG = Number("2000");
  static MAX_LENGTH_SMALL = Number("320");

  /**
  * array validation
  * not null and not undefined
  * has key "length"
  * value of "length" valid integer
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static array(value) {
    try {
      return (
        ValidationUtils.exists(value) &&
        hasProperty.call(value, "length") &&
        ValidationUtils.integer(value.length)
      );
    }
    catch { return false; }
  }

  /**
  * @param {any} value
  * @returns {Boolean}
  */
  static boolean(value) {
    try {
      return (
        ValidationUtils.exists(value) &&
        (String(value) === "true" ||
         String(value) === "false")
      );
    }
    catch { return false; }
  }

  /**
  * colorHex validation
  * valid string
  * first character must be a "#" character
  * length equals 4 or length equals 7
  * all characters must be one of the following:
  * - "A", "B", "C", "D", "E", "F", "#",
  * - "0", "1", "2", "3", "4", "5", "6", "7", "8", "9"
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static colorHex(value) {
    try {
      //  loose validity
      const isLooselyValid = (
        ValidationUtils.string(value) &&
        String(value).startsWith("#") &&
        (String(value).length === Number("4") ||
         String(value).length === Number("7"))
      );
      if(!isLooselyValid) return false;

      // strict validity
      const validCharacters = [
        "A", "B", "C", "D", "E", "F", "#",
        "0", "1", "2", "3", "4", "5", "6", "7", "8", "9"
      ];
      let isCharactersValid = true;
      [...String(value).toUpperCase()].forEach((character) => {
        if(!validCharacters.includes(String(character))) {
          isCharactersValid = false;
        }
      });
      return isCharactersValid;
    }
    catch { return false; }
  }

  /**
  * colorRgb validation
  * valid integer
  * maximum value of 255
  * minimum value of 0
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static colorRgb(value) {
    try {
      return (
        ValidationUtils.integer(value) &&
        Number(value) <= Number("255") &&
        Number(value) >= Number("0")
      );
    }
    catch { return false; }
  }

  /**
  * email validation
  * valid string, less than 320 characters
  * does not contain whitespace
  * must contains only 1 "@" character
  * must contain at least 1 "." character
  * must have at least 1 character before the "@" character
  * must have at least 3 characters after the "@" character
  * suffix must contain at least 1 "." character
  * suffix must contain at least 1 character before, after, and between "." characters
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static email(value) {
    try {
      //  loose validity
      const isLooselyValid = (
        ValidationUtils.stringSmall(value) &&
        String(value).trim().length === String(value).length &&
        String(value).split("@").length === Number("2") &&
        String(value).split(".").length >= Number("2")
      );
      if(!isLooselyValid) return false;

      // strict validity
      const [prefix, suffix] = String(value).split("@");

      // prefix
      const isPrefixValid = prefix.length >= Number("1");
      if(!isPrefixValid) return false;

      // suffix (as a whole)
      const isSuffixWholeValid = (
        suffix.length >= Number("3") &&
        suffix.split(".").length >= Number("2")
      );
      if(!isSuffixWholeValid) return false;

      // suffix (in parts)
      const splitSuffix = suffix.split(".");
      for(const index in splitSuffix) {
        if(!splitSuffix[index] || splitSuffix[index].length < Number("1")) {
          return false;
        }
      }
      return true;
    }
    catch { return false; }
  }

  /**
  * exists validation
  * not null and not undefined
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static exists(value) {
    try {
      return (
        value !== null &&
        value !== undefined &&
        String(value) !== "null" &&
        String(value) !== "undefined"
      );
    }
    catch { return false; }
  }

  /**
  * float validation
  * valid number
  * is a parsable float
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static float(value) {
    try {
      return (
        ValidationUtils.number(value) &&
        !isNaN(parseFloat(value))
      );
    }
    catch { return false; }
  }

  /**
  * imageSources validation
  * valid strings
  * at least one of the following sizes:
  *  - full
  *  - large
  *  - medium
  *  - small
  *  - thumb
  *
  * @param {any} value
  * @returns {Boolean}
  */
   static imageSources(value) {
    try {
      const isLooselyValid = ValidationUtils.exists(value);
      if(!isLooselyValid) return false;

      // source sizes
      const sizes = [
        "full",
        "large",
        "medium",
        "small",
        "thumb",
      ];

      let hasValidSize = false;
      for(const index in sizes) {
        if(sizes[index]) {
          const size = sizes[index];
          if(ValidationUtils.string(value[size])) {
            hasValidSize = true;
            break;
          }
        }
      }
      return hasValidSize;
    }
    catch { return false; }
  }

  /**
  * integer validation
  * valid number
  * is a parsable integer
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static integer(value) {
    try {
      return (
        ValidationUtils.number(value) &&
        !isNaN(parseInt(value))
      );
    }
    catch { return false; }
  }

  /**
  * latitude, longitude validation
  * not null and not undefined
  * has key "lat"
  * has key "lng"
  * "lat" is a valid float
  * "lng" is a valid float
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static latLng(value) {
    try {
      return (
        ValidationUtils.exists(value) &&
        hasProperty.call(value, "lat") &&
        hasProperty.call(value, "lng") &&
        ValidationUtils.float(value.lat) &&
        ValidationUtils.float(value.lng)
      );
    }
    catch { return false; }
  }

  /**
  * notificationType validation
  * valid string
  * value equals one of the following:
  * - "primary"
  * - "secondary"
  * - "info"
  * - "success"
  * - "warning"
  * - "danger"
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static notificationType(value) {
    try {
      return (
        ValidationUtils.string(value) && (
          String(value) === "primary" ||
          String(value) === "secondary" ||
          String(value) === "info" ||
          String(value) === "success" ||
          String(value) === "warning" ||
          String(value) === "danger"
        )
      );
    }
    catch { return false; }
  }

  /**
  * number validation
  * valid string
  * only contains number characters
  *  - 0 through 9
  *  - "-", ",", & "."
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static number(value) {
    try {
      //  loose validity
      const isLooselyValid = ValidationUtils.string(value);
      if(!isLooselyValid) return false;

      // strict validity
      const validCharacters = [
        "0", "1", "2", "3", "4", "5", "6", "7", "8", "9",
        "-", ",", "."
      ];
      let isCharactersValid = true;
      [...String(value)].forEach((character) => {
        if(!validCharacters.includes(String(character))) {
          isCharactersValid = false;
        }
      });
      return isCharactersValid;
    }
    catch { return false; }
  }

  /**
  * password validation
  * valid string, less than 320 characters
  * at least 8 characters
  * cannot contain whitespace
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static password(value) {
    try {
      return (
        ValidationUtils.stringSmall(value) &&
        String(value).length >= Number("8") &&
        String(value).trim().length === String(value).length
      );
    }
    catch { return false; }
  }

  /**
  * pov validation
  * not null and not undefined
  * has key "heading"
  * has key "pitch"
  * has key "zoom"
  * "heading" is a valid float
  * "pitch" is a valid float
  * "zoom" is a valid float
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static pov(value) {
    try {
      return (
        ValidationUtils.exists(value) &&
        hasProperty.call(value, "heading") &&
        hasProperty.call(value, "pitch") &&
        hasProperty.call(value, "zoom") &&
        ValidationUtils.float(value.heading) &&
        ValidationUtils.float(value.pitch) &&
        ValidationUtils.float(value.zoom)
      );
    }
    catch { return false; }
  }

  /**
  * streetView validation
  * not null and not undefined
  * has key "position"
  * has key "pov"
  * "position" is a valid latLng
  * "pov" is a valid pov
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static streetView(value) {
    try {
      return (
        ValidationUtils.exists(value) &&
        hasProperty.call(value, "position") &&
        hasProperty.call(value, "pov") &&
        ValidationUtils.latLng(value.position) &&
        ValidationUtils.pov(value.pov)
      );
    }
    catch { return false; }
  }

  /**
  * string validation
  * not null and not undefined
  * string is not empty or only whitespace
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static string(value) {
    try {
      return (
        ValidationUtils.exists(value) &&
        String(value).trim() !== ""
      );
    }
    catch { return false; }
  }

  /**
  * stringAllowEmpty validation
  * not null and not undefined
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static stringAllowEmpty(value) {
    try {
      return (ValidationUtils.exists(value));
    }
    catch { return false; }
  }

  /**
  * stringBig validation
  * valid string
  * string length is <= 2000
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static stringBig(value) {
    try {
      return (
        ValidationUtils.string(value) &&
        String(value).length <= ValidationUtils.MAX_LENGTH_BIG
      );
    }
    catch { return false; }
  }

  /**
  * stringSmall validation
  * valid string
  * string length is <= 320
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static stringSmall(value) {
    try {
      return (
        ValidationUtils.string(value) &&
        String(value).length <= ValidationUtils.MAX_LENGTH_SMALL
      );
    }
    catch { return false; }
  }

  /**
  * url validation
  * valid string, less than 320 characters
  * does not contain whitespace
  * must contain at least 1 "." character
  * must have at least 1 character before the last "." character
  * must have at least 1 character after the last "." character
  * must contain at least 1 character before, after, and between all "." characters
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static url(value) {
    try {
      //  loose validity
      const isLooselyValid = (
        ValidationUtils.stringSmall(value) &&
        String(value).trim().length === String(value).length &&
        String(value).split(".").length >= Number("2")
      );
      if(!isLooselyValid) return false;

      // strict validity
      const prefix = String(value).substring(0, String(value).lastIndexOf("."));
      const suffix = String(value).substring(String(value).lastIndexOf(".") + 1);

      // prefix
      const isPrefixValid = prefix.length >= Number("1");
      if(!isPrefixValid) return false;

      // suffix
      const isSuffixValid = suffix.length >= Number("1");
      if(!isSuffixValid) return false;

      // multi-domain (more than 1 ".")
      const splitDomains = String(value).split(".");
      for(const index in splitDomains) {
        if(!splitDomains[index] || splitDomains[index].length < Number("1")) {
          return false;
        }
      }
      return true;
    }
    catch { return false; }
  }

  /**
  * uuid validation
  * valid stringSmall
  * handled by uuide node_module
  *
  * @param {any} value
  * @returns {Boolean}
  */
  static uuid(value) {
    try {
      return (
        ValidationUtils.stringSmall(value) &&
        UuidUtils.validate(value)
      );
    }
    catch { return false; }
  }
}
export default ValidationUtils;
